import React, { useEffect, useRef, useState } from 'react';
import grapesjs from 'grapesjs';
import 'grapesjs/dist/css/grapes.min.css';

interface GrapesJSEditorProps {
  initialContent: string;
  onSave: (content: string) => void;
  onClose: () => void;
  baseUrl?: string;
}

const GrapesJSEditor: React.FC<GrapesJSEditorProps> = ({
  initialContent,
  onSave,
  onClose,
  baseUrl
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [useFallback, setUseFallback] = useState(false);
  const [htmlContent, setHtmlContent] = useState(initialContent);
  const [showPreview, setShowPreview] = useState(true);
  const [loadingStep, setLoadingStep] = useState('Initializing...');

  useEffect(() => {
    if (!editorRef.current) return;

    console.log('GrapesJSEditor: Starting initialization...');
    setLoadingStep('Checking GrapesJS availability...');

    // Set a timeout for fallback (5 seconds)
    const fallbackTimeout = setTimeout(() => {
      console.log('GrapesJS loading timeout (5s), using fallback');
      setUseFallback(true);
      setIsLoading(false);
    }, 5000);

    const initializeGrapesJS = async () => {
      try {
        setLoadingStep('Loading GrapesJS library...');
        console.log('GrapesJSEditor: Loading GrapesJS library...');
        
        // Check if grapesjs is available
        if (typeof grapesjs === 'undefined') {
          throw new Error('GrapesJS library not available');
        }

        setLoadingStep('Creating editor container...');
        console.log('GrapesJSEditor: Creating editor container...');
        
        // Ensure the container is ready
        if (!editorRef.current) {
          throw new Error('Editor container not available');
        }

        setLoadingStep('Initializing GrapesJS editor...');
        console.log('GrapesJSEditor: Initializing GrapesJS editor...');

        // Simple GrapesJS initialization
        const editor = grapesjs.init({
          container: editorRef.current as HTMLElement,
          height: '100%',
          width: 'auto',
          storageManager: false,
          assetManager: {
            embedAsBase64: true,
            assets: []
          },
          deviceManager: {
            devices: [
              { name: 'Desktop', width: '' },
              { name: 'Tablet', width: '768px', widthMedia: '992px' },
              { name: 'Mobile', width: '320px', widthMedia: '480px' }
            ]
          },
          canvas: {
            styles: ['https://cdn.jsdelivr.net/npm/bootstrap@5.1.3/dist/css/bootstrap.min.css']
          },
          cssIcons: 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css',
          blockManager: {
            appendTo: '.gjs-blocks-c',
            blocks: [
              {
                id: 'section',
                label: 'Section',
                category: 'Basic',
                content: '<section class="section"><h2>This is a section</h2><p>This is a box</p></section>',
              },
              {
                id: 'text',
                label: 'Text',
                category: 'Basic',
                content: '<div data-gjs-type="text">Insert your text here</div>',
              },
              {
                id: 'image',
                label: 'Image',
                category: 'Basic',
                content: { type: 'image' },
                activate: true,
              },
              {
                id: 'button',
                label: 'Button',
                category: 'Basic',
                content: '<button class="btn btn-primary">Click me</button>',
              }
            ]
          },
          layerManager: {
            appendTo: '.gjs-layers-c'
          },
          styleManager: {
            appendTo: '.gjs-styles-c',
            sectors: [
              {
                name: 'General',
                open: false,
                buildProps: ['float', 'display', 'position', 'top', 'right', 'left', 'bottom']
              },
              {
                name: 'Dimension',
                open: false,
                buildProps: ['width', 'height', 'max-width', 'min-height', 'margin', 'padding']
              },
              {
                name: 'Typography',
                open: false,
                buildProps: ['font-family', 'font-size', 'font-weight', 'letter-spacing', 'color', 'line-height', 'text-align', 'text-decoration', 'text-shadow']
              },
              {
                name: 'Decorations',
                open: false,
                buildProps: ['border-radius', 'border', 'box-shadow', 'background']
              }
            ]
          }
        });

        setLoadingStep('Adding custom commands...');
        console.log('GrapesJSEditor: Adding custom commands...');

        // Add custom save command
        editor.Commands.add('save-content', {
          run: (editor: any) => {
            const html = editor.getHtml();
            const css = editor.getCss();
            const fullContent = `<style>${css}</style>${html}`;
            onSave(fullContent);
          }
        });

        setLoadingStep('Loading initial content...');
        console.log('GrapesJSEditor: Loading initial content...');

        // Load initial content
        if (initialContent && initialContent.trim()) {
          try {
            // Extract body content if it's a full HTML document
            let contentToLoad = initialContent;
            if (initialContent.includes('<body>')) {
              const bodyMatch = initialContent.match(/<body[^>]*>([\s\S]*)<\/body>/i);
              if (bodyMatch) {
                contentToLoad = bodyMatch[1];
              }
            }
            
            // Clean and sanitize the content for GrapesJS
            contentToLoad = contentToLoad
              .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove scripts
              .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '') // Remove style tags
              .replace(/on\w+="[^"]*"/gi, '') // Remove event handlers
              .replace(/on\w+='[^']*'/gi, '') // Remove event handlers
              .replace(/javascript:/gi, '') // Remove javascript: protocols
              .replace(/data-src/gi, 'src') // Convert data-src to src for images
              .replace(/srcset="[^"]*"/gi, '') // Remove srcset attributes
              .replace(/sizes="[^"]*"/gi, ''); // Remove sizes attributes
            
            // If content is too complex, use a simplified version
            if (contentToLoad.length > 50000) {
              console.log('GrapesJSEditor: Content too large, using simplified version');
              contentToLoad = `
                <div style="padding: 20px; background: #f8f9fa; border-radius: 8px;">
                  <h2>Captured Content (Simplified)</h2>
                  <p>The captured content was too complex for direct editing. Here's a simplified version:</p>
                  <div style="max-height: 400px; overflow-y: auto; border: 1px solid #ddd; padding: 15px; background: white;">
                    ${contentToLoad.substring(0, 10000)}...
                  </div>
                  <p style="margin-top: 15px; color: #666;">
                    <strong>Note:</strong> You can now edit this content using the GrapesJS editor tools.
                  </p>
                </div>
              `;
            }
            
            editor.setComponents(contentToLoad);
            console.log('GrapesJSEditor: Initial content loaded successfully');
          } catch (contentError) {
            console.error('GrapesJSEditor: Error loading initial content:', contentError);
            editor.setComponents(`
              <section class="section">
                <h1>Welcome to GrapesJS Editor</h1>
                <p>Start building your page by adding components from the left panel.</p>
                <div class="container">
                  <div class="row">
                    <div class="col-md-6">
                      <h3>Feature 1</h3>
                      <p>Drag and drop components to build your page.</p>
                    </div>
                    <div class="col-md-6">
                      <h3>Feature 2</h3>
                      <p>Edit styles and properties in the right panel.</p>
                    </div>
                  </div>
                </div>
              </section>
            `);
          }
        } else {
          editor.setComponents(`
            <section class="section">
              <h1>Welcome to GrapesJS Editor</h1>
              <p>Start building your page by adding components from the left panel.</p>
              <div class="container">
                <div class="row">
                  <div class="col-md-6">
                    <h3>Feature 1</h3>
                    <p>Drag and drop components to build your page.</p>
                  </div>
                  <div class="col-md-6">
                    <h3>Feature 2</h3>
                    <p>Edit styles and properties in the right panel.</p>
                  </div>
                </div>
              </div>
            </section>
          `);
        }

        setLoadingStep('Finalizing editor setup...');
        console.log('GrapesJSEditor: Editor initialized successfully');
        
        // Small delay to ensure everything is rendered
        setTimeout(() => {
          clearTimeout(fallbackTimeout);
          setIsLoading(false);
          console.log('GrapesJSEditor: Setup complete, editor ready!');
        }, 500);

      } catch (err) {
        console.error('GrapesJSEditor: Error initializing GrapesJS:', err);
        clearTimeout(fallbackTimeout);
        setError(err instanceof Error ? err.message : 'Unknown error');
        setUseFallback(true);
        setIsLoading(false);
      }
    };

    // Start initialization
    initializeGrapesJS();

    return () => {
      clearTimeout(fallbackTimeout);
    };
  }, [initialContent, onSave, baseUrl]);

  const handleSave = () => {
    if (useFallback) {
      onSave(htmlContent);
    } else {
      console.log('Save triggered from GrapesJS');
    }
  };

  const handleContentChange = (newContent: string) => {
    setHtmlContent(newContent);
  };

  const addComponent = (componentType: string) => {
    let newComponent = '';
    
    switch (componentType) {
      case 'heading':
        newComponent = '<h2 style="color: #333; margin: 20px 0;">New Heading</h2>';
        break;
      case 'paragraph':
        newComponent = '<p style="line-height: 1.6; color: #555; margin: 15px 0;">New paragraph text here. Click to edit.</p>';
        break;
      case 'button':
        newComponent = '<button style="background: #007bff; color: white; padding: 10px 20px; border: none; border-radius: 5px; cursor: pointer; margin: 10px 0;">Click Me</button>';
        break;
      case 'image':
        newComponent = '<img src="https://via.placeholder.com/300x200" alt="Placeholder Image" style="max-width: 100%; height: auto; margin: 15px 0;" />';
        break;
      case 'divider':
        newComponent = '<hr style="border: 1px solid #ddd; margin: 20px 0;" />';
        break;
      case 'container':
        newComponent = '<div style="padding: 20px; border: 1px solid #ddd; margin: 10px 0; border-radius: 5px; background: #f8f9fa;"><h3 style="color: #333; margin-bottom: 10px;">Container</h3><p style="color: #666;">This is a container element.</p></div>';
        break;
      case 'card':
        newComponent = '<div style="background: white; border: 1px solid #ddd; border-radius: 8px; padding: 20px; margin: 15px 0; box-shadow: 0 2px 4px rgba(0,0,0,0.1);"><h3 style="color: #333; margin-bottom: 10px;">Card Title</h3><p style="color: #666; margin-bottom: 15px;">This is a card component with some content.</p><button style="background: #007bff; color: white; padding: 8px 16px; border: none; border-radius: 4px; cursor: pointer;">Learn More</button></div>';
        break;
      default:
        newComponent = '<div style="padding: 10px; border: 1px solid #ddd; margin: 10px 0;">New element</div>';
    }

    setHtmlContent(prev => prev + '\n' + newComponent);
  };

  const forceFallback = () => {
    setUseFallback(true);
    setIsLoading(false);
  };

  if (isLoading) {
    return (
      <div style={{ 
        height: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#f8f9fa'
      }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5>Loading GrapesJS Editor...</h5>
          <p className="text-muted">{loadingStep}</p>
          <small className="text-muted d-block mb-3">If this takes too long, we'll switch to a simpler editor</small>
          <button className="btn btn-outline-primary btn-sm" onClick={forceFallback}>
            <i className="fas fa-bolt me-1"></i>
            Switch to Simple Editor Now
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        height: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#f8f9fa'
      }}>
        <div className="text-center">
          <div className="text-danger mb-3">
            <i className="fas fa-exclamation-triangle fa-3x"></i>
          </div>
          <h5>Error Loading GrapesJS Editor</h5>
          <p className="text-muted">{error}</p>
          <button className="btn btn-primary" onClick={forceFallback}>
            Use Simple Editor Instead
          </button>
        </div>
      </div>
    );
  }

  // Fallback Simple Editor
  if (useFallback) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Toolbar */}
        <div className="p-3 border-bottom bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              <i className="fas fa-edit me-2"></i>
              Simple HTML Editor (GrapesJS Fallback)
            </h6>
            <div>
              <button 
                className={`btn btn-sm me-2 ${showPreview ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setShowPreview(!showPreview)}
              >
                <i className="fas fa-eye me-1"></i>
                {showPreview ? 'Hide' : 'Show'} Preview
              </button>
              <button className="btn btn-sm btn-success me-2" onClick={handleSave}>
                <i className="fas fa-save me-1"></i>
                Save
              </button>
              <button className="btn btn-sm btn-outline-danger" onClick={onClose}>
                <i className="fas fa-times me-1"></i>
                Close
              </button>
            </div>
          </div>
        </div>

        {/* Editor Content */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
          {/* Component Library */}
          <div style={{ width: '200px', borderRight: '1px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
            <div className="p-3 border-bottom">
              <h6 className="mb-2">Components</h6>
              <small className="text-muted">Click to add</small>
            </div>
            <div className="p-2">
              <button 
                className="btn btn-sm btn-outline-secondary w-100 mb-2"
                onClick={() => addComponent('heading')}
              >
                <i className="fas fa-heading me-1"></i>
                Heading
              </button>
              <button 
                className="btn btn-sm btn-outline-secondary w-100 mb-2"
                onClick={() => addComponent('paragraph')}
              >
                <i className="fas fa-paragraph me-1"></i>
                Paragraph
              </button>
              <button 
                className="btn btn-sm btn-outline-secondary w-100 mb-2"
                onClick={() => addComponent('button')}
              >
                <i className="fas fa-square me-1"></i>
                Button
              </button>
              <button 
                className="btn btn-sm btn-outline-secondary w-100 mb-2"
                onClick={() => addComponent('image')}
              >
                <i className="fas fa-image me-1"></i>
                Image
              </button>
              <button 
                className="btn btn-sm btn-outline-secondary w-100 mb-2"
                onClick={() => addComponent('divider')}
              >
                <i className="fas fa-minus me-1"></i>
                Divider
              </button>
              <button 
                className="btn btn-sm btn-outline-secondary w-100 mb-2"
                onClick={() => addComponent('container')}
              >
                <i className="fas fa-box me-1"></i>
                Container
              </button>
            </div>
          </div>

          {/* HTML Editor */}
          <div style={{ width: showPreview ? '30%' : '100%', borderRight: showPreview ? '1px solid #dee2e6' : 'none' }}>
            <div className="p-3 border-bottom bg-light">
              <h6 className="mb-0">HTML Code</h6>
              <small className="text-muted">Edit the HTML directly</small>
            </div>
            <textarea
              value={htmlContent}
              onChange={(e) => handleContentChange(e.target.value)}
              style={{
                width: '100%',
                height: 'calc(100% - 60px)',
                border: 'none',
                padding: '15px',
                fontFamily: 'monospace',
                fontSize: '13px',
                resize: 'none',
                outline: 'none',
                backgroundColor: '#f8f9fa'
              }}
              placeholder="Edit HTML content here..."
            />
          </div>
          
          {/* Live Preview */}
          {showPreview && (
            <div style={{ width: '70%', overflow: 'auto' }}>
              <div className="p-3 border-bottom bg-light">
                <h6 className="mb-0">Live Preview</h6>
                <small className="text-muted">See how your changes look</small>
              </div>
              <div style={{ padding: '20px', height: 'calc(100% - 60px)', overflow: 'auto' }}>
                <div 
                  dangerouslySetInnerHTML={{ __html: htmlContent }}
                  style={{ 
                    width: '100%', 
                    minHeight: '100%',
                    backgroundColor: 'transparent'
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // GrapesJS Editor
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <div className="p-3 border-bottom bg-light">
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0">GrapesJS Visual Editor</h6>
          <div>
            <button className="btn btn-sm btn-success me-2" onClick={handleSave}>
              <i className="fas fa-save me-1"></i>
              Save
            </button>
            <button className="btn btn-sm btn-outline-danger" onClick={onClose}>
              <i className="fas fa-times me-1"></i>
              Close
            </button>
          </div>
        </div>
      </div>

      {/* GrapesJS Editor */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <div ref={editorRef} style={{ height: '100%' }} />
      </div>
    </div>
  );
};

export default GrapesJSEditor;
